// src/components/OwnerGuard.jsx
// Schützt Charakter-Routen vor Zugriff durch Nicht-Besitzer (Frontend-Check)
// Nutzt useAuth() für den User-State – kein separater Supabase Auth-Call

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { protokoll } from '../lib/protokoll';

/**
 * OwnerGuard - Prüft ob der eingeloggte User Besitzer des Charakters ist
 *
 * @param {React.ReactNode} children - Die zu schützende Komponente
 */
export default function OwnerGuard({ children }) {
  const { id } = useParams(); // Charakter-ID aus URL
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Warten bis Auth-State verfügbar ist
    if (authLoading) return;

    // Kein User → AuthGuard hat bereits weitergeleitet, trotzdem absichern
    if (!user) {
      navigate('/', { replace: true });
      return;
    }

    // Ohne ID gibt es nichts zu pruefen; das Rendern unten wertet das aus.
    if (!id) return;

    let isMounted = true;

    const checkOwnership = async () => {
      try {
        // Charakter-Ownership per DB-Query prüfen (RLS ist aktiv)
        const { data: character, error: dbError } = await supabase
          .from('character')
          .select('id, player_id')
          .eq('id', id)
          .maybeSingle();

        if (!isMounted) return;

        if (dbError) {
          protokoll.fehler('Zugriffsschutz', 'DB Fehler', dbError);
          navigate('/character', { replace: true });
          return;
        }

        if (!character) {
          protokoll.warnung(
            'Zugriffsschutz',
            'Kein Charakter gefunden (RLS blockiert oder nicht vorhanden)'
          );
          navigate('/character', { replace: true });
          return;
        }

        // Expliziter Ownership-Check
        if (character.player_id !== user.id) {
          // Zugriff verweigert
          navigate('/character', { replace: true });
          return;
        }

        // Alles OK
        setAuthorized(true);
      } catch (err) {
        protokoll.fehler('Zugriffsschutz', 'OwnerGuard Fehler', err);
        if (isMounted) navigate('/character', { replace: true });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    checkOwnership();

    return () => {
      isMounted = false;
    };
  }, [id, navigate, user, authLoading]);

  if (authLoading || (id && loading)) {
    return <LoadingSpinner message="Prüfe Berechtigung..." />;
  }

  if (!authorized) {
    return null;
  }

  return children;
}
